import { AutomationStep, ConditionalStep, ConditionDataOutput } from '@/types/automationSteps.ts';
import * as React from 'react';
import { SegmentedControl, Stack, Text } from '@mantine/core';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import SingleChildDataInputSelector from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleChildDataInputSelector.tsx';
import { DEFAULT_DYNAMIC_DATA_INPUT } from '@/utils/constants.ts';
import { ConditionAction, SchemaValidationMode } from '@/types/automationFields.ts';
import SingleDataInputSelectorInner from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleDataInputSelectorInner.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import JsonSchemaRefSelect from '@/pages/Editor/components/SharedFields/JsonSchemaRefSelect.tsx';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';

type props = {
  conditionDataOutput: ConditionDataOutput;
  conditionAction: ConditionAction;
  onChangeNoDelay: (updatedInput: ConditionDataOutput) => void;
  step: ConditionalStep;
  conditionIndex: number;
  dataOutputIndex: number;
  isElseCondition: boolean;
  childSteps: AutomationStep[];
};

const ConditionDataOutputFields = ({
  conditionDataOutput,
  conditionAction,
  onChangeNoDelay,
  step,
  conditionIndex,
  dataOutputIndex,
  isElseCondition,
  childSteps,
}: props) => {
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const invalidChildOutput = useStepSpecificError(
    step,
    StepErrorType.InvalidChildOutput,
    conditionIndex,
    dataOutputIndex,
  );

  const invalidDataInput = useStepSpecificError(
    step,
    StepErrorType.InvalidDataInput,
    conditionIndex,
    dataOutputIndex,
  );
  const invalidLiteralOutput = useStepSpecificError(
    step,
    StepErrorType.InvalidConditionalLiteralOutput,
    conditionIndex,
    dataOutputIndex,
  );

  const conditionalDataOutput = step.data_outputs.find(
    (co) => co.output_id === conditionDataOutput.output_id,
  );
  const literalMode = conditionDataOutput.literal_json !== null && conditionDataOutput.literal_json !== undefined;
  const [literalText, setLiteralText] = React.useState(
    literalMode ? JSON.stringify(conditionDataOutput.literal_json, null, 2) : '{}',
  );
  const [literalError, setLiteralError] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (literalMode) {
      setLiteralText(JSON.stringify(conditionDataOutput.literal_json, null, 2));
    }
  }, [conditionDataOutput.literal_json, literalMode]);

  if (!conditionalDataOutput) {
    return null;
  }

  return (
    <>
      {conditionAction === ConditionAction.ReturnDataset && (
        <Stack gap="xs">
          <SegmentedControl
            aria-label={conditionalDataOutput.name + ' - Output source'}
            value={literalMode ? 'literal' : 'dataset'}
            data={[
              { label: 'Existing dataset', value: 'dataset' },
              { label: 'Literal JSON object', value: 'literal' },
            ]}
            onChange={(value) => {
              setLiteralError(undefined);
              if (value === 'literal') {
                onChangeNoDelay({
                  ...conditionDataOutput,
                  data_input: DEFAULT_DYNAMIC_DATA_INPUT,
                  literal_json: conditionDataOutput.literal_json ?? {},
                });
                return;
              }
              onChangeNoDelay({
                ...conditionDataOutput,
                data_input: conditionDataOutput.data_input ?? DEFAULT_DYNAMIC_DATA_INPUT,
                literal_json: null,
              });
            }}
          />
          {literalMode ? (
            <>
              <StyledTextArea
                label={conditionalDataOutput.name + ' - Literal JSON object'}
                value={literalText}
                description="Materialized as a JSON output when this branch is selected."
                autosize
                minRows={4}
                error={literalError || invalidLiteralOutput}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  setLiteralText(value);
                  try {
                    const parsed: unknown = JSON.parse(value);
                    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
                      setLiteralError('Literal output must be a JSON object.');
                      return;
                    }
                    setLiteralError(undefined);
                    onChangeNoDelay({
                      ...conditionDataOutput,
                      data_input: DEFAULT_DYNAMIC_DATA_INPUT,
                      literal_json: parsed as Record<string, unknown>,
                    });
                  } catch {
                    setLiteralError('Enter valid JSON.');
                  }
                }}
              />
              <Text size="xs" c="dimmed">
                A parent and branch schema are required and validate this value before execution.
              </Text>
            </>
          ) : (
            <SingleDataInputSelectorInner
              step={step}
              dataInputError={invalidDataInput}
              dataInput={conditionDataOutput.data_input ?? DEFAULT_DYNAMIC_DATA_INPUT}
              onChange={(dataInput) =>
                onChangeNoDelay({ ...conditionDataOutput, data_input: dataInput, literal_json: null })
              }
              label={conditionalDataOutput.name + ' - Data Input'}
              includeEmptyDatasets={isElseCondition}
            />
          )}
        </Stack>
      )}
      {conditionAction === ConditionAction.ChildSteps && (
        <SingleChildDataInputSelector
          step={step}
          label={conditionalDataOutput.name + ' - Child Output'}
          dataInput={conditionDataOutput.child_data_output}
          onChange={(dataInput) =>
            onChangeNoDelay({
              ...conditionDataOutput,
              child_data_output: dataInput || DEFAULT_DYNAMIC_DATA_INPUT,
              literal_json: null,
            })
          }
          dataInputError={invalidChildOutput}
          required={true}
          allowedStepIds={childSteps.map((child) => child.step_id)}
        />
      )}
      {(conditionAction === ConditionAction.ChildSteps ||
        conditionAction === ConditionAction.ReturnDataset) && (
        <JsonSchemaRefSelect
          label={conditionalDataOutput.name + ' - Branch schema'}
          description="The schema expected from this selected branch before it is promoted."
          schemas={latestVersion?.schemas ?? []}
          value={conditionDataOutput.schema_ref}
          requiredValidationMode={SchemaValidationMode.Error}
          onChange={(schema_ref) => onChangeNoDelay({ ...conditionDataOutput, schema_ref })}
        />
      )}
    </>
  );
};

export default ConditionDataOutputFields;
